import { supabase } from '../lib/supabase';

export type DashboardMetrics = {
  assessments: number;
  launches: number;
  students: number;
  skills: number;
};

export type PerformanceSummary = {
  average: number;
  high: number;
  medium: number;
  low: number;
  evaluated: number;
};

export type RecentAssessment = {
  id: string;
  name: string;
  grade: number;
  subject: string;
  status: string;
  assessment_date: string | null;
};

export type DashboardData = {
  metrics: DashboardMetrics;
  performance: PerformanceSummary;
  recentAssessments: RecentAssessment[];
};

function pct(part: number, total: number) {
  return total ? Math.round((part / total) * 100) : 0;
}

export async function loadDashboardData(schoolId: string): Promise<DashboardData> {
  if (!supabase) throw new Error('Supabase não configurado.');

  const assessmentsCount = supabase
    .from('assessments')
    .select('*', { count: 'exact', head: true })
    .eq('school_id', schoolId);

  const launchesCount = supabase
    .from('launches')
    .select('*', { count: 'exact', head: true })
    .eq('school_id', schoolId);

  const studentsCount = supabase
    .from('students')
    .select('*', { count: 'exact', head: true })
    .eq('school_id', schoolId)
    .eq('status', 'ACTIVE');

  const skillsCount = supabase
    .from('matrix_library')
    .select('*', { count: 'exact', head: true })
    .eq('active', true)
    .or(`school_id.is.null,school_id.eq.${schoolId}`);

  const performanceQuery = supabase
    .from('student_assessment_results')
    .select('percentage')
    .eq('school_id', schoolId);

  const recentQuery = supabase
    .from('assessments')
    .select('id,name,grade,subject,status,assessment_date,created_at')
    .eq('school_id', schoolId)
    .order('assessment_date', { ascending: false, nullsFirst: false })
    .order('created_at', { ascending: false })
    .limit(5);

  const [assessments, launches, students, skills, performance, recent] = await Promise.all([
    assessmentsCount,
    launchesCount,
    studentsCount,
    skillsCount,
    performanceQuery,
    recentQuery,
  ]);

  const firstError = assessments.error || launches.error || students.error || skills.error || performance.error || recent.error;
  if (firstError) throw firstError;

  const values: number[] = (performance.data || [])
    .map((row: { percentage: number | null }) => Number(row.percentage))
    .filter((value: number) => Number.isFinite(value));
  const total = values.length;
  const average = total ? Math.round(values.reduce((sum: number, value: number) => sum + value, 0) / total) : 0;
  const highCount = values.filter((value: number) => value >= 80).length;
  const mediumCount = values.filter((value: number) => value >= 60 && value < 80).length;
  const lowCount = values.filter((value: number) => value < 60).length;

  return {
    metrics: {
      assessments: assessments.count || 0,
      launches: launches.count || 0,
      students: students.count || 0,
      skills: skills.count || 0,
    },
    performance: {
      average,
      high: pct(highCount, total),
      medium: pct(mediumCount, total),
      low: pct(lowCount, total),
      evaluated: total,
    },
    recentAssessments: (recent.data || []) as RecentAssessment[],
  };
}
